import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { listingsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { MapPin, Building2, Clock, Star, ShoppingCart, Shield, CheckCircle, ArrowLeft, FileText, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isBuyer } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await listingsAPI.getById(id);
        setListing(response.data);
        if (response.data.pricing_tiers && response.data.pricing_tiers.length > 0) {
          setSelectedTier(response.data.pricing_tiers[0]);
        }
      } catch (error) {
        toast.error('Failed to load listing');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handlePurchase = () => {
    if (!isAuthenticated) {
      toast.info('Please log in to purchase leads');
      navigate('/login');
      return;
    }
    if (!isBuyer) {
      toast.error('Only buyers can purchase leads');
      return;
    }
    navigate('/checkout/' + id + '?tier=' + selectedTier.name);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!listing) return null;

  const regions = listing.regions || [];
  const industries = listing.industries || [];
  const jobTitles = listing.job_titles || [];
  const leadFields = listing.lead_fields || [];
  const pricingTiers = listing.pricing_tiers || [];

  return (
    <Layout>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="listing-detail-page">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate('/marketplace'); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        <div className="flex flex-col lg:flex-row gap-8 mb-8">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline">{listing.category}</Badge>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                <Shield className="w-3 h-3 mr-1" />
                GDPR Compliant
              </Badge>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3">{listing.title}</h1>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <div className="flex items-center">
                <Building2 className="w-4 h-4 mr-1" />
                {listing.company_name}
              </div>
              <div className="flex items-center">
                <Star className="w-4 h-4 mr-1 text-amber-500 fill-amber-500" />
                {listing.avg_rating} rating
              </div>
              <div className="flex items-center">
                <ShoppingCart className="w-4 h-4 mr-1" />
                {listing.total_orders} orders
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">About This Offering</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed">{listing.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Targeting Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center text-sm font-medium text-slate-700 mb-2">
                    <MapPin className="w-4 h-4 mr-2" />
                    Regions
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {regions.map(function(region) {
                      return <Badge key={region} variant="secondary">{region}</Badge>;
                    })}
                  </div>
                </div>
                <Separator />
                <div>
                  <div className="flex items-center text-sm font-medium text-slate-700 mb-2">
                    <Building2 className="w-4 h-4 mr-2" />
                    Industries
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {industries.map(function(industry) {
                      return <Badge key={industry} variant="secondary">{industry}</Badge>;
                    })}
                  </div>
                </div>
                <Separator />
                <div>
                  <div className="flex items-center text-sm font-medium text-slate-700 mb-2">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Job Titles
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {jobTitles.map(function(title) {
                      return <Badge key={title} variant="outline">{title}</Badge>;
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Lead Fields Provided</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {leadFields.map(function(field) {
                    return (
                      <div key={field} className="flex items-center text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                        {field.replace(/_/g, ' ')}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Compliance & Policies</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-sky-600" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Delivery SLA</div>
                    <div className="text-sm text-slate-600">{listing.delivery_sla_days} business days</div>
                  </div>
                </div>
                {listing.replacement_policy && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">Replacement Policy</div>
                      <div className="text-sm text-slate-600">{listing.replacement_policy}</div>
                    </div>
                  </div>
                )}
                {listing.consent_method && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">Consent Method</div>
                      <div className="text-sm text-slate-600">{listing.consent_method}</div>
                    </div>
                  </div>
                )}
                {listing.compliance_notes && (
                  <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
                    <div className="font-medium text-slate-700 mb-1">Compliance Notes</div>
                    {listing.compliance_notes}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="shadow-medium">
                <CardHeader>
                  <CardTitle className="text-lg">Select Package</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pricingTiers.map(function(tier) {
                    const isSelected = selectedTier && selectedTier.name === tier.name;
                    return (
                      <div
                        key={tier.name}
                        className={'pricing-tier ' + (isSelected ? 'selected' : '')}
                        onClick={function() { setSelectedTier(tier); }}
                        data-testid={'tier-' + tier.name.toLowerCase()}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-semibold text-slate-900">{tier.name}</div>
                            <div className="text-sm text-slate-500">{tier.quantity} leads</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg text-slate-900">${tier.price.toLocaleString()}</div>
                            <div className="text-xs text-slate-500">${tier.cpl}/lead</div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex items-center text-sm text-sky-600 mt-2">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Selected
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Separator />

                  {selectedTier && (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Package</span>
                        <span className="font-medium">{selectedTier.name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Leads</span>
                        <span className="font-medium">{selectedTier.quantity}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">CPL</span>
                        <span className="font-medium">${selectedTier.cpl}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-lg">${selectedTier.price.toLocaleString()}</span>
                      </div>
                    </div>
                  )}

                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={handlePurchase}
                    data-testid="purchase-btn"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Continue to Checkout
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    You'll be able to add targeting and campaign details in the next step
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ListingDetailPage;
